import React, {useMemo} from "react";
import {Html} from "@react-three/drei";
import {CAMERA_VIEW} from "../module3d/cameraViews";
import {
    getModulePriorityLabel,
    resolveModulePriorityMarkerLocalPose,
} from "./compositionModulePriorityMarker3dUtils";

const PRIORITY_BADGE_DISTANCE_FACTOR = {
    default: {perspective: 8, orthographic: 10},
    compact: {perspective: 6, orthographic: 7.5},
};

const priorityBadgeStyle = (label, compact = false) => {
    if (compact) {
        return {
            minWidth: label.length > 1 ? "17px" : "14px",
            height: "14px",
            paddingLeft: label.length > 1 ? "3px" : 0,
            paddingRight: label.length > 1 ? "3px" : 0,
            borderRadius: label.length > 1 ? "7px" : "50%",
            backgroundColor: "#006CCC",
            border: "1px solid #FFFFFF",
            boxSizing: "border-box",
            display: "grid",
            placeItems: "center",
            color: "#FFFFFF",
            fontSize: "10px",
            fontWeight: 400,
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
            pointerEvents: "none",
            userSelect: "none",
        };
    }
    return {
        minWidth: label.length > 1 ? "22px" : "18px",
        height: "18px",
        paddingLeft: label.length > 1 ? "4px" : 0,
        paddingRight: label.length > 1 ? "4px" : 0,
        borderRadius: label.length > 1 ? "9px" : "50%",
        backgroundColor: "#006CCC",
        border: "1px solid #FFFFFF",
        boxSizing: "border-box",
        display: "grid",
        placeItems: "center",
        color: "#FFFFFF",
        fontSize: "12px",
        fontWeight: 400,
        lineHeight: 1,
        fontVariantNumeric: "tabular-nums",
        pointerEvents: "none",
        userSelect: "none",
    };
};

/**
 * Ортография: Html transform — плашка в плоскости грани.
 * Перспектива: Html без transform (проекция 3D-точки); transform+CSS3D «уезжает» вперёд, mesh здесь не виден.
 */
const CompositionModulePriorityMarker3d = ({
    row,
    moduleSizeM,
    visible = true,
    cameraView = CAMERA_VIEW.PERSPECTIVE,
    compact = false,
}) => {
    const isPerspective = cameraView === CAMERA_VIEW.PERSPECTIVE;
    const distanceFactors = compact
        ? PRIORITY_BADGE_DISTANCE_FACTOR.compact
        : PRIORITY_BADGE_DISTANCE_FACTOR.default;
    const priorityLabel = getModulePriorityLabel(row);
    const pose = useMemo(
        () => resolveModulePriorityMarkerLocalPose({
            moduleSizeM,
            coordinateX3DPoint: row?.coordinateX3DPoint,
            coordinateY3DPoint: row?.coordinateY3DPoint,
        }),
        [
            moduleSizeM,
            row?.coordinateX3DPoint,
            row?.coordinateY3DPoint,
        ],
    );

    if (!visible || priorityLabel == null) {
        return null;
    }

    return (
        <group
            position={pose.position}
            rotation={isPerspective ? [0, 0, 0] : pose.rotation}
            frustumCulled={false}
            userData={{module3dRole: "helper"}}
        >
            {isPerspective ? (
                <Html
                    center
                    distanceFactor={distanceFactors.perspective}
                    zIndexRange={[40, 0]}
                    style={{pointerEvents: "none"}}
                >
                    <div style={priorityBadgeStyle(priorityLabel, compact)}>
                        {priorityLabel}
                    </div>
                </Html>
            ) : (
                <Html
                    transform
                    center
                    distanceFactor={distanceFactors.orthographic}
                    zIndexRange={[40, 0]}
                    style={{pointerEvents: "none"}}
                >
                    <div style={priorityBadgeStyle(priorityLabel, compact)}>
                        {priorityLabel}
                    </div>
                </Html>
            )}
        </group>
    );
};

export default CompositionModulePriorityMarker3d;
